import { SlugTypes, LibraryContentTypes } from '~/types/adamTypes';

import { getServices } from '~/db/services';

import type {
  LibrarySchema,
  LibraryBookSchema,
  MediaSchema,
} from '~/types/schema';

import { NomadDB } from '~/db/NomadDB';

import { getSlugType } from '~/lib/utils/slug';
interface Ids {
  libraryBooksIds: string[];
  libraryBoxIds: string[];
}

export const arrayToObject = (list, key = 'id'): Object => {
  return list.reduce((acc, cur) => {
    const k = cur[key];
    acc[k] = cur;
    return acc;
  }, {});
};

const _getChildren = (parents: any[]): any[] => {
  let children = [];

  parents.forEach((pChildren) => {
    if (pChildren.children) children = [...pChildren.children, ...children];
  });
  return children;
};

export const getChildrenAtDetph = (data, depth, start = 0): any[] => {
  let children = data ? data.children || [] : [];
  for (let i = start; i < depth; ++i) {
    children = [..._getChildren(children)];
  }
  return children;
};

export const getParentsAtDepth = (data, depth): any[] => {
  let parents = [data];

  for (let i = 0; i < depth; ++i) {
    parents = [..._getChildren(parents)];
  }
  return parents;
};

export const getChildrenIds = (data, depth = 0): Ids => {
  const libraryBoxIds = [];
  const libraryBooksIds = [];
  const children = getChildrenAtDetph(data, depth, 0);
  children.forEach((child: any) => {
    switch (child.contentType) {
      case LibraryContentTypes.LibraryBook:
        libraryBooksIds.push(child.id);
        break;
      case LibraryContentTypes.LibraryBox:
        libraryBoxIds.push(child.id);
        break;
      default:
        // TODO add other types
        if (globalThis.$logger)
          globalThis.$logger.warn(
            'Populate Library, type',
            child.type,
            'not known'
          );
        break;
    }
  });
  return { libraryBoxIds, libraryBooksIds };
};

export const populateIds = async (
  $db: NomadDB,
  ids: Ids
): Promise<{ mLibraryBoxs: object; mLibraryBooks: object }> => {
  const { libraryBooksService, librariesService } = getServices($db);

  const { libraryBoxIds, libraryBooksIds } = ids;

  const libraryBooks = await libraryBooksService.findByIds(libraryBooksIds);

  const libraryBoxs = await librariesService.findByIds(libraryBoxIds);

  const mLibraryBooks = arrayToObject(libraryBooks);

  const mLibraryBoxs = arrayToObject(libraryBoxs);

  return { mLibraryBooks, mLibraryBoxs };
};

const _populateAtDepth = async (
  $db: NomadDB,
  data,
  depth = 0
): Promise<LibrarySchema> => {
  // children ids
  const ids = getChildrenIds(data, depth);
  const { mLibraryBooks, mLibraryBoxs } = await populateIds($db, ids);
  const parents = getParentsAtDepth(data, depth);
  parents.forEach((parent) => {
    if (parent.children) {
      const children = [...parent.children].reduce(
        (
          acc: { libraries: any[]; libraryBooks: any[]; libraryBoxs: any[] },
          child: any
        ) => {
          switch (child.contentType) {
            case LibraryContentTypes.LibraryBook:
              if (mLibraryBooks[child.id]) {
                acc.libraryBooks.push(mLibraryBooks[child.id]);
                acc.libraries.push(mLibraryBooks[child.id]);
              }
              break;
            case LibraryContentTypes.LibraryBox:
              if (mLibraryBoxs[child.id]) {
                acc.libraryBoxs.push(mLibraryBoxs[child.id]);
                acc.libraries.push(mLibraryBoxs[child.id]);
              }
              break;
            default:
          }
          return acc;
        },
        <{ libraries: any[]; libraryBooks: any[]; libraryBoxs: any[] }>{
          libraries: [],
          libraryBooks: [],
          libraryBoxs: [],
        }
      );
      parent.children = children.libraries;
      parent.libraryBooks = children.libraryBooks;
      parent.libraryBoxs = children.libraryBoxs;
    }
  });
  return data;
};
// RETRIVE CATEGORIES CHILDREN IN DEPTH
const _populate = async (
  $db: NomadDB,
  data,
  depth = 0,
  deep = 1
): Promise<LibrarySchema> => {
  do {
    await _populateAtDepth($db, data, depth);
    depth++;
  } while (depth < deep);

  return data;
};

const _getParents = async (
  $db: NomadDB,
  library: LibrarySchema,
  parents: { slug?: string; type?: string }[]
): Promise<LibrarySchema> => {
  const parentParam = parents.pop();

  const { librariesService } = getServices($db);

  if (parentParam) {
    const parentData = await librariesService.get(parentParam);
    library.parents = [parentData, ...library.parents];
    if (parents.length > 0) {
      library = await _getParents($db, library, parents);
    }
  }
  return library;
};
//
// RETRIEVE CHILDREN AT DEPTH
//
export const populate = async (
  $db: NomadDB,
  params,
  deep = 1,
  parents: { slug?: string; type?: string }[] = []
): Promise<LibrarySchema | LibraryBookSchema> => {
  let data;

  const { libraryBooksService, librariesService } = getServices($db);

  if (params.slug && getSlugType(params.slug) === SlugTypes.App) {
    data = await libraryBooksService.get(params);
  } else {
    data = await librariesService.get(params);
    if (typeof data !== 'undefined') {
      data = await _populate($db, data, 0, deep);
    } else {
      data = null;
    }
  }
  // Add parents

  if (data) {
    data.parents = [];
    if (parents.length > 0) {
      data = await _getParents($db, data, parents);
    }
  }

  return data;
};

const _retrieveMediaIds = (
  data: (LibrarySchema | LibraryBookSchema)[],
  acc = []
): Array<string> => {
  data.forEach((item) => {
    const library = item as LibrarySchema;
    if (library) {
      if (library.icon) {
        acc.push(library.icon);
      }
      if (library.children) {
        acc = [..._retrieveMediaIds(library.children, acc)];
      }
    }
    const libraryBook = item as LibraryBookSchema;
    if (libraryBook) {
      if (libraryBook.metaIcon) {
        acc.push(libraryBook.metaIcon);
      }
    }
  });
  return acc;
};

export const retrieveMedia = async (
  $db,
  data:
    | (LibrarySchema | LibraryBookSchema)[]
    | LibrarySchema
    | LibraryBookSchema
): Promise<Map<string, MediaSchema>> => {
  data = !Array.isArray(data) ? [data] : data;
  const { mediasService } = getServices($db);
  const mediaIds = _retrieveMediaIds(data);
  const media = await mediasService.findByIds(mediaIds);
  const mediaMap = media.reduce(
    (acc: Map<string, MediaSchema>, cur: MediaSchema) => {
      acc.set(cur.id, cur);
      return acc;
    },
    new Map<string, MediaSchema>()
  );
  return mediaMap;
};
